import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Store } from "@ngrx/store";
import { Subject, takeUntil } from "rxjs";

import {
  filterByLogon,
  setEmptyData,
  loadLogonsList,
} from "../../../users/state/actions/user.actions";
import { selectErrorListing } from "../../../users/state/selectors/user.selectors";
import {
  IUserAddSearchMessagesHelper,
  UserAddSearchMessagesHelper,
} from "../../../users/components/user-add/helpers/user-add-search-messages.helper";
import { Utils, SearchInfo } from "@serasa/shared/utils/utils";

@Component({
    selector: "pl-search-form",
    templateUrl: "./search-form.component.html",
    styleUrls: ["./search-form.component.scss"],
    standalone: false
})
export class SearchFormComponent implements OnInit, OnDestroy {
  @ViewChild('sedsSearch', { read: ElementRef }) sedsSearchRef!: ElementRef;

  private readonly destroyed$ = new Subject<void>();

  public inputError = false;
  public errorMessage = "";
  public searchApiError: IUserAddSearchMessagesHelper | null = null;
  public searchValue = "";
  public searchExecuted = false;

  constructor(private readonly store: Store) {}

  public ngOnInit(): void {
    this.store
      .select(selectErrorListing)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error: HttpErrorResponse | null | undefined) => {
        if (error && this.searchExecuted) {
          if (UserAddSearchMessagesHelper.isTooManyResultsError(error)) {
            this.searchApiError = null;
            this.inputError = true;
            this.errorMessage =
              UserAddSearchMessagesHelper.TOO_MANY_RESULTS_INLINE_MESSAGE;
          } else {
            this.inputError = false;
            this.errorMessage = '';
            this.searchApiError = new UserAddSearchMessagesHelper(error).error;
          }
          return;
        }

        if (!error) {
          this.searchApiError = null;
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public validateSearchValue(value: string): boolean {
    if (!value || !value.trim()) {
      this.inputError = false;
      this.errorMessage = "";
      return true;
    }

    const trimmedValue = value.trim();

    if (trimmedValue.includes('@')) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(trimmedValue)) {
        this.inputError = true;
        this.errorMessage = "Digite um nome, e-mail ou logon válido";
        return false;
      }
    }

    const looksLikeLogon = /^\w{7,8}$/.test(trimmedValue);
    if (looksLikeLogon && !/\d/.test(trimmedValue)) {
      this.inputError = true;
      this.errorMessage = "Digite um nome, e-mail ou logon válido";
      return false;
    }

    this.inputError = false;
    this.errorMessage = "";
    return true;
  }

  public onSearchInput(event: Event): void {
    if ((event as KeyboardEvent).key === 'Enter') {
      return;
    }

    const target = event.target as HTMLInputElement | null;
    const newValue = target?.value !== undefined
      ? target.value
      : this.getSearchInputValue();
    const hadExecutedSearch = this.searchExecuted;

    this.searchValue = newValue;

    if (hadExecutedSearch) {
      this.searchExecuted = false;
      this.searchApiError = null;
      this.inputError = false;
      this.errorMessage = '';
    }

    if (!newValue.trim() && hadExecutedSearch) {
      localStorage.removeItem("searchValue");
      localStorage.removeItem("searchType");
      this.store.dispatch(
        loadLogonsList({ offset: 0, limit: 20, isPagination: false }),
      );
    }

    this.validateSearchValue(this.searchValue);
  }

  public onSearchSubmit(searchText: string): void {
    if (!searchText?.trim()) {
      return;
    }

    this.searchUser(searchText, false);
  }

  public onSearchButtonClick(): void {
    this.searchUser(this.getSearchInputValue(), false);
  }

  public searchUser(searchText: string, reset: boolean): void {
    this.searchValue = searchText || "";

    if (reset) {
      this._clearSearch();
      return;
    }

    if (!this.validateSearchValue(this.searchValue)) {
      localStorage.removeItem("searchValue");
      localStorage.removeItem("searchType");
      return;
    }

    if (!this.searchValue.trim()) {
      localStorage.removeItem("searchValue");
      localStorage.removeItem("searchType");
      this.store.dispatch(setEmptyData());
      this.searchExecuted = false;
      this.searchApiError = null;
      return;
    }

    const searchInfo: SearchInfo = Utils.detectSearchType(this.searchValue);

    localStorage.setItem("searchValue", this.searchValue);
    localStorage.setItem("searchType", searchInfo.type);

    this.searchExecuted = true;
    this.searchApiError = null;

    this.store.dispatch(
      filterByLogon({
        searchValue: searchInfo.value,
        searchType: searchInfo.type
      })
    );
  }

  private _clearSearch(): void {
    localStorage.removeItem("searchValue");
    localStorage.removeItem("searchType");
    const input = this.sedsSearchRef?.nativeElement?.querySelector('input') as HTMLInputElement;
    if (input) {
      input.value = "";
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.searchValue = "";
    this.inputError = false;
    this.errorMessage = "";
    this.searchApiError = null;
    this.searchExecuted = false;

    this.store.dispatch(loadLogonsList({ offset: 0, limit: 20, isPagination: false }));
  }

  private getSearchInputValue(): string {
    const input = this.sedsSearchRef?.nativeElement?.querySelector(
      'input',
    ) as HTMLInputElement | null;

    return input?.value ?? this.searchValue ?? '';
  }
}
